/* whatsappLinks — shared helpers for opening pre-formatted WhatsApp messages
 * from the doctor's number. All flows here are "doctor sends a message via
 * their own WhatsApp" — no Twilio, no platform integration, zero per-message
 * cost. The patient sees the message coming from their clinic, not from us.
 *
 * Each helper returns a fully-encoded `https://wa.me/{digits}?text=…` URL
 * the caller can pass to `window.open(url, '_blank')`. If the patient has
 * no usable phone number, returns null and the caller should toast a hint.
 */

function _digits(raw) {
    return (raw || "").replace(/\D/g, "");
}

function _encode(text) {
    return encodeURIComponent(text);
}

function _fmtDate(iso) {
    if (!iso) return "";
    // Accept either "YYYY-MM-DD" or full ISO; we want a humane "Tue, 5 Mar".
    const d = iso.length === 10 ? new Date(`${iso}T00:00:00`) : new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "long" });
}

/* Builds a WhatsApp link for an appointment confirmation / reminder.
 *
 * `appt`  — { patient_name, patient_phone, date ("YYYY-MM-DD"), time,
 *             appointment_type | type, notes }
 * `clinic` — { name, address }
 *
 * Status doesn't gate the helper — the doctor decides when to send. The copy
 * is friendly-confirmatory: works equally well as a 24h reminder or a
 * post-booking confirmation. */
export function buildAppointmentWhatsappLink(appt, clinic = {}) {
    const phone = _digits(appt?.patient_phone);
    if (!phone) return null;

    const clinicName = (clinic.name || "your clinic").trim();
    const clinicAddress = (clinic.address || "").trim();
    const apptType = (appt.appointment_type || appt.type || "appointment").replace(/_/g, " ");
    const dateText = _fmtDate(appt.date);

    const lines = [];
    lines.push(`Hello ${appt.patient_name || ""},`);
    lines.push("");
    lines.push(`Reminder of your ${apptType} at *${clinicName}*:`);
    lines.push("");
    lines.push(`*Date:* ${dateText}`);
    if (appt.time) lines.push(`*Time:* ${appt.time}`);
    if (clinicAddress) lines.push(`*Location:* ${clinicAddress}`);
    if (appt.notes) {
        lines.push("");
        lines.push(`Note: ${appt.notes}`);
    }
    lines.push("");
    lines.push("Please reply to confirm, or let us know if you need to reschedule.");
    lines.push("");
    lines.push("Thank you,");
    lines.push(clinicName);

    return `https://wa.me/${phone}?text=${_encode(lines.join("\n"))}`;
}
